import * as React from 'react';
import { styled } from '@linaria/react';
import { toggle } from 'opticks';
import { ButtonLink } from '@daedalus/atlas/ButtonLink';
import { Divider } from '@daedalus/atlas/Divider';
import ContentWrapper from '@daedalus/atlas/helpers/ContentWrapper';
import { Grid } from '@daedalus/atlas/helpers/Grid';
import { Price } from '@daedalus/atlas/Price';
import { Tag } from '@daedalus/atlas/Tag';
import { Text } from '@daedalus/atlas/Text';
import { Page } from '@daedalus/core/src/analytics/types/Events';
import { BoardType } from '@daedalus/core/src/api-types/bovio/response/booking';
import { Price as BookingPrice } from '@daedalus/core/src/api-types/bovio/response/booking';
import { defaultCurrencyCode } from '@daedalus/core/src/localization/business/currency';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { getCancellationDeadline, getRefundType } from '@daedalus/core/src/offer/business/cancellationPenalties';
import { CancellationPenalty } from '@daedalus/core/src/offer/types/offer';
import { OfferPrice } from '@daedalus/core/src/offer/types/offer';
import { CurrencyDisplayVariant } from '@daedalus/core/src/price/business/formatPrice';
import { getDisplayPrice, getPriceCurrencyCode, getPriceDetails } from '@daedalus/core/src/price/business/price';
import { OfferDetails } from '../OfferDetails';
import { getTotalChargeLaterDifferentialPrice } from '../OfferDetails/utils';
import { RoomDetailsSectionSkeleton, RoomPriceSkeleton } from './RoomDetailsSectionSkeleton';
interface Props {
  /** Simplified version of RoomDetailsSection */
  showRoomDetails?: boolean;
  /** Number of nights the room is booked for */
  numberOfNights: number;
  /** Name of the room */
  roomName: string;
  /** Callback for opening room details overlay    */
  openRoomDetailsOverlay?: () => void;
  /** Callback for opening cancellation details overlay    */
  onCancellationButtonClicked?: () => void;
  /** Numbers of rooms */
  numberOfRooms: number;
  /** Index of the room */
  roomIndex?: number;
  /** The type of meal/board included in the booking */
  boardType?: BoardType;
  /** Wether it's fetching offer check data */
  isLoading?: boolean;
  /** Offer prices */
  offerPrices: OfferPrice[] | BookingPrice[];
  /** Is canPayLater eligible flag */
  canPayLater?: boolean;
  /** Offer cancellation penalties */
  cancellationPenalties?: CancellationPenalty[];
  /** First name of the room's main guest */
  firstName?: string;
  /** Last name of the room's main guest */
  lastName?: string;
  //** The hotel's time zone */
  hotelTimeZone?: string;
  includeNumberOfRooms?: boolean;
  /** Indicates if the offer is eligible for Charge Later */
  isChargeLaterEligible?: boolean;
  /** Indicates if the user has selected the charge later option in payment page */
  isChargeLaterSelected?: boolean;
  /** The page where the component is being rendered at */
  page?: Page;
  /** The date on which the user will be charged for the booking */
  chargeLaterDate?: Date;
  /**
   * Indicate if it's a charge later booking.
   */
  isChargeLaterBooking?: boolean;
  /** Callback for opening charge later details overlay */
  onChargeLaterLabelClicked?: () => void;
  /** Indicates the expected click on charge later label action */
  chargeLaterClickAction?: 'bottomSheet' | 'tooltip';
  /**
   * If the rooms should be shown grouped. This way, instead of showing the room index,
   * it will show the total number of rooms, as `2x RoomName`
   */
  groupRoomDetails?: boolean;
  /** display divider at the end of section */
  withDivider?: boolean;
  currencyDisplayVariant?: CurrencyDisplayVariant;
  isPostBooking?: boolean;
}
interface RoomCounterProps {
  numberOfRooms: number;
  roomNumber: number;
  groupRoomDetails?: boolean;
}
interface PriceComponentProps {
  currencyCode: string;
  priceValue: number;
  currencyDisplayVariant?: CurrencyDisplayVariant;
}
interface RoomPriceProps {
  numberOfNights: Props['numberOfNights'];
  isLoading: Props['isLoading'];
  includeNumberOfRooms: Props['includeNumberOfRooms'];
  roomPricePerNight: number;
  currencyCode: string;
  numberOfRooms: number;
  totalPricePerRoom: number;
  showAverageNightlyPriceText?: boolean;
  /**
   * If true, the total price will be the sum of all rooms, not for a single room.
   */
  showPriceOfAllRooms?: boolean;
  currencyDisplayVariant?: CurrencyDisplayVariant;
}
const NUMBER_OF_DECIMALS_FOR_PRICE = 2;
const _exp = /*#__PURE__*/() => ButtonLink;
const ButtonLinkWrapper = /*#__PURE__*/styled(_exp())({
  name: "ButtonLinkWrapper",
  class: "b17xudec",
  propsAsIs: true
});
const BasePriceWrapper = /*#__PURE__*/styled('div')({
  name: "BasePriceWrapper",
  class: "b1wgnjz0",
  propsAsIs: false
});
const RoomNameWrapper = /*#__PURE__*/styled('div')({
  name: "RoomNameWrapper",
  class: "rif78ek",
  propsAsIs: false
});
const _exp4 = /*#__PURE__*/() => Grid;
const RoomTitleWrapper = /*#__PURE__*/styled(_exp4())({
  name: "RoomTitleWrapper",
  class: "rhil0w4",
  propsAsIs: true
});
const _exp6 = /*#__PURE__*/() => Text;
const PriceTextWrapper = /*#__PURE__*/styled(_exp6())({
  name: "PriceTextWrapper",
  class: "pa7iwhw",
  propsAsIs: true
});
const RoomMultiplierWrapper = /*#__PURE__*/styled('div')({
  name: "RoomMultiplierWrapper",
  class: "rekd90p",
  propsAsIs: false
});
export const RoomCounter = ({
  numberOfRooms,
  roomNumber,
  groupRoomDetails
}: RoomCounterProps) => <Tag variant="neutral">
    <Text variant="labelXS" colorPath="content.neutral.c800">
      {groupRoomDetails || numberOfRooms == 1 ? <FormattedMessageWrapper id="room.yourRooms" defaultMessage="{numberOfRooms, plural, one {Your room} other {Your rooms}}" values={{
      numberOfRooms
    }} /> : <FormattedMessageWrapper id="room.reference" defaultMessage="Room {roomNumber}" values={{
      roomNumber
    }} />}
    </Text>
  </Tag>;
const PriceComponent = ({
  priceValue,
  currencyCode,
  currencyDisplayVariant
}: PriceComponentProps) => {
  return <Price noStyle maximumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE} minimumFractionDigits={NUMBER_OF_DECIMALS_FOR_PRICE} value={priceValue} currency={currencyCode} currencyDisplayVariant={currencyDisplayVariant} />;
};
export const RoomPrice = ({
  isLoading,
  roomPricePerNight,
  currencyCode,
  numberOfRooms,
  numberOfNights,
  includeNumberOfRooms,
  totalPricePerRoom,
  showAverageNightlyPriceText = false,
  showPriceOfAllRooms = false,
  currencyDisplayVariant
}: RoomPriceProps) => {
  const totalPrice = showPriceOfAllRooms ? totalPricePerRoom * numberOfRooms : totalPricePerRoom;
  return <Grid container gap="s500" spacing="s000" justify="space-between">
      {isLoading ? <RoomPriceSkeleton /> : <>
          <PriceTextWrapper variant="bodyM" tag="div">
            <BasePriceWrapper>
              <PriceComponent priceValue={roomPricePerNight} currencyCode={currencyCode} currencyDisplayVariant={currencyDisplayVariant} />
            </BasePriceWrapper>
            {showAverageNightlyPriceText && <FormattedMessageWrapper id="splitBooking.avgNightlyPrice" defaultMessage="avg. nightly price" />}
            <RoomMultiplierWrapper>
              <FormattedMessageWrapper id="payment.priceAndNight" defaultMessage="{basePrice} x {nights, plural, one {1 night} other {# nights}}" values={{
            basePrice: '',
            nights: numberOfNights
          }} />
              {numberOfRooms > 1 && includeNumberOfRooms && <FormattedMessageWrapper id="payment.roomsPostFix" defaultMessage="x {rooms, plural, one {1 room} other {# rooms}}" values={{
            rooms: numberOfRooms
          }} />}
            </RoomMultiplierWrapper>
          </PriceTextWrapper>
          <Text variant="bodyM" tag="div">
            <PriceComponent priceValue={totalPrice} currencyCode={currencyCode} currencyDisplayVariant={currencyDisplayVariant} />
          </Text>
        </>}
    </Grid>;
};
export const RoomDetailsSection = ({
  openRoomDetailsOverlay,
  offerPrices,
  cancellationPenalties = [],
  canPayLater = false,
  numberOfNights,
  numberOfRooms,
  roomName,
  boardType,
  isLoading = false,
  firstName,
  lastName,
  hotelTimeZone,
  roomIndex = 0,
  showRoomDetails = true,
  onCancellationButtonClicked,
  includeNumberOfRooms = false,
  isChargeLaterEligible,
  isChargeLaterSelected,
  page,
  chargeLaterDate,
  isChargeLaterBooking,
  onChargeLaterLabelClicked,
  chargeLaterClickAction,
  groupRoomDetails,
  withDivider = true,
  currencyDisplayVariant,
  isPostBooking
}: Props) => {
  if (isLoading) return <RoomDetailsSectionSkeleton />;
  const displayPrice = getDisplayPrice<OfferPrice | BookingPrice>(offerPrices);
  if (!displayPrice) return null;
  const freeCancellationDeadline = getCancellationDeadline(cancellationPenalties);
  const displayCurrencyCode = getPriceCurrencyCode(displayPrice);
  const currencyCode = displayCurrencyCode || defaultCurrencyCode;
  const taxesDisplayPrice = toggle('50feb427-cl-differential-price', Number(displayPrice.chargeable.taxes), isChargeLaterSelected ? getTotalChargeLaterDifferentialPrice(Number(displayPrice.chargeable.taxes)) : Number(displayPrice.chargeable.taxes));
  const baseRateDisplayPrice = Number(displayPrice.chargeable.base);
  const roomNumber = roomIndex + 1;
  const roomPricePerNightBase = baseRateDisplayPrice / numberOfNights / numberOfRooms;
  const roomPricePerNight = toggle('50feb427-cl-differential-price', roomPricePerNightBase, isChargeLaterSelected ? getTotalChargeLaterDifferentialPrice(roomPricePerNightBase) : roomPricePerNightBase);
  const totalPricePerRoomBase = includeNumberOfRooms ? baseRateDisplayPrice : baseRateDisplayPrice / numberOfRooms;
  const totalPricePerRoom = toggle('50feb427-cl-differential-price', totalPricePerRoomBase, isChargeLaterSelected ? getTotalChargeLaterDifferentialPrice(totalPricePerRoomBase) : totalPricePerRoomBase);
  const totalPriceWithTax = toggle('50feb427-cl-differential-price', baseRateDisplayPrice + taxesDisplayPrice, isChargeLaterSelected ? getTotalChargeLaterDifferentialPrice(baseRateDisplayPrice + taxesDisplayPrice) : baseRateDisplayPrice + taxesDisplayPrice);
  const roomTitle = showRoomDetails ? roomName : `${numberOfRooms}x ${roomName}`;
  const isRoomCounterVisible = showRoomDetails && roomNumber;
  const {
    totalAtPropertyDisplayPrice,
    totalUpFrontDisplayPrice,
    chargeableCurrencyCode
  } = getPriceDetails(offerPrices, canPayLater);
  const isMultiRoomBundleVisible = groupRoomDetails && numberOfRooms > 1;
  const refundType = getRefundType(cancellationPenalties);
  return <ContentWrapper paddingTop="s500">
      {isRoomCounterVisible && <RoomCounter numberOfRooms={numberOfRooms} roomNumber={roomNumber} groupRoomDetails={groupRoomDetails} />}
      <ContentWrapper paddingBottom={showRoomDetails ? 's300' : 's100'} paddingTop="s300">
        <RoomTitleWrapper container spacing="s000" direction="row" justify="space-between" alignItems="flex-start" gap="s500">
          <RoomNameWrapper>
            {roomName && <>
                {isMultiRoomBundleVisible ? <Text variant="titleM" data-testid="room-name-title">
                    <FormattedMessageWrapper id="bundleRoomName" defaultMessage={'{numberOfRooms}x {roomName}'} values={{
                numberOfRooms,
                roomName
              }} />
                  </Text> : <Text variant="titleM" data-testid="room-name-title">
                    {roomTitle}
                  </Text>}
              </>}
          </RoomNameWrapper>
          {openRoomDetailsOverlay ? <ButtonLinkWrapper onClick={openRoomDetailsOverlay} size="sm" isCompact dataId="room-details-button">
              <Text variant="labelS" tag="span">
                <FormattedMessageWrapper id="details" defaultMessage="Details" />
              </Text>
            </ButtonLinkWrapper> : null}
        </RoomTitleWrapper>
        {showRoomDetails && <OfferDetails refundType={refundType} payLater={canPayLater} onCancellationButtonClick={onCancellationButtonClicked} boardType={boardType} freeCancellationDeadline={freeCancellationDeadline} cancellationPenalties={cancellationPenalties} totalPrice={totalPriceWithTax} totalUpFrontDisplayPrice={totalUpFrontDisplayPrice} currencyCode={currencyCode} chargeableCurrencyCode={chargeableCurrencyCode} hotelTimeZone={hotelTimeZone} isChargeLaterEligible={isChargeLaterEligible} isChargeLaterSelected={isChargeLaterSelected} page={page} chargeLaterDate={chargeLaterDate} onChargeLaterLabelClicked={onChargeLaterLabelClicked} chargeLaterClickAction={chargeLaterClickAction} payAtPropertyPrice={totalAtPropertyDisplayPrice} isChargeLaterBooking={isChargeLaterBooking} isPostBooking={isPostBooking} />}
      </ContentWrapper>
      {firstName && lastName && <ContentWrapper paddingTop="s300" paddingBottom="s400">
          <Text variant="bodyS" className="fs-mask">
            <FormattedMessageWrapper id="roomReservationDetails.names" defaultMessage="Reserved for {firstName} {lastName}" values={{
          firstName,
          lastName
        }} />
          </Text>
        </ContentWrapper>}
      <ContentWrapper paddingTop={showRoomDetails ? 's200' : 's000'} paddingBottom="s500">
        <RoomPrice currencyCode={currencyCode} numberOfNights={numberOfNights} numberOfRooms={numberOfRooms} roomPricePerNight={roomPricePerNight} totalPricePerRoom={totalPricePerRoom} showPriceOfAllRooms={isMultiRoomBundleVisible} includeNumberOfRooms={includeNumberOfRooms || isMultiRoomBundleVisible} isLoading={isLoading} currencyDisplayVariant={currencyDisplayVariant} />
      </ContentWrapper>
      {withDivider && <Divider />}
    </ContentWrapper>;
};

require("./RoomDetailsSection.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./RoomDetailsSection.tsx");